// AudioGuide.tsx — Inline audio guidance player (no popup, no repeat)
// MP3 files live in: public/audio/
// Usage: <AudioGuide src="/audio/guide-birth-details.mp3" label="Audio Guide" />

import React, { useRef, useState, useEffect, useCallback } from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Tooltip from '@mui/joy/Tooltip';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';

export interface AudioGuideProps {
  /** Path relative to public folder, e.g. "/audio/guide-birth-details.mp3" */
  src: string;
  /** Label shown next to the button */
  label?: string;
  /** Tooltip override */
  title?: string;
  /** Size variant */
  size?: 'sm' | 'md';
  /** 'default' = subtle pill | 'prominent' = coloured header button | 'red' = red gradient button */
  variant?: 'default' | 'prominent' | 'red';
  /** Called on every audio timeupdate event with the current playback position in seconds */
  onTimeUpdate?: (currentTime: number) => void;
}

export default function AudioGuide({ src, label = 'Audio Guide', title, size = 'sm', variant = 'default', onTimeUpdate }: AudioGuideProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Create audio element once; re-bind if src changes
  useEffect(() => {
    if (!audioRef.current) audioRef.current = new Audio();
    const audio = audioRef.current;
    if (audio.src !== window.location.origin + src) {
      audio.src = src;
      audio.preload = 'none';
    }
    const onEnded = () => { setIsPlaying(false); setIsPaused(false); };
    audio.addEventListener('ended', onEnded);
    return () => audio.removeEventListener('ended', onEnded);
  }, [src]);

  // Wire timeupdate to onTimeUpdate callback
  useEffect(() => {
    if (!onTimeUpdate) return;
    const audio = audioRef.current;
    if (!audio) return;
    const handler = () => onTimeUpdate(audio.currentTime);
    audio.addEventListener('timeupdate', handler);
    return () => audio.removeEventListener('timeupdate', handler);
  }, [onTimeUpdate]);

  // Stop and release on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  // Play from start (or resume after pause)
  const handlePlayResume = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPaused) {
      // Resume from current position
      audio.play().catch(() => {});
      setIsPlaying(true);
      setIsPaused(false);
    } else {
      // Fresh play from start
      audio.currentTime = 0;
      audio.play().catch(() => {});
      setIsPlaying(true);
      setIsPaused(false);
    }
  }, [isPaused]);

  // Pause — preserves currentTime so Resume works correctly
  const handlePause = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    setIsPlaying(false);
    setIsPaused(true);
  }, []);

  // Stop — resets to beginning
  const handleStop = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
    setIsPaused(false);
  }, []);

  const iconSize = size === 'sm' ? '1.05rem' : '1.3rem';
  const active = isPlaying || isPaused;
  const tooltipLabel = title ?? (isPlaying ? 'Pause audio' : isPaused ? 'Resume audio' : `${label} — click to play`);
  const isProminent = variant === 'prominent';
  const isRed = variant === 'red';

  return (
    <Tooltip title={tooltipLabel} placement="top" arrow>
      <Box
        onClick={isPlaying ? handlePause : handlePlayResume}
        role="button"
        aria-label={tooltipLabel}
        aria-pressed={isPlaying}
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); isPlaying ? handlePause(e as any) : handlePlayResume(); } }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          cursor: 'pointer',
          userSelect: 'none',
          borderRadius: '20px',
          px: isProminent ? 1.4 : 1.1,
          py: isProminent ? '5px' : '3px',
          border: '1px solid',
          borderColor: isRed
            ? 'transparent'
            : isProminent
            ? (active ? 'success.400' : 'success.300')
            : (active ? 'primary.300' : 'neutral.outlinedBorder'),
          bgcolor: isRed
            ? 'transparent'
            : isProminent
            ? (active ? 'success.400' : 'success.softBg')
            : (active ? 'primary.softBg' : 'background.surface'),
          background: isRed
            ? (active ? 'linear-gradient(135deg, #6B0000 0%, #B01030 50%, #CC1177 100%)' : 'linear-gradient(135deg, #8B0000 0%, #DC143C 50%, #FF1493 100%)')
            : undefined,
          transition: 'all 0.18s ease',
          '&:hover': {
            borderColor: isRed ? 'transparent' : isProminent ? 'success.500' : 'primary.300',
            bgcolor: isRed ? 'transparent' : isProminent ? 'success.400' : 'primary.softBg',
            background: isRed ? 'linear-gradient(135deg, #6B0000 0%, #B01030 50%, #CC1177 100%)' : undefined,
          },
          ...(isProminent && !active && {
            animation: 'audioGuidePulseProminent 2.2s ease-in-out infinite',
            '@keyframes audioGuidePulseProminent': {
              '0%, 100%': { boxShadow: '0 0 0 0 rgba(34,197,94,0.35)', opacity: 1 },
              '50%':       { boxShadow: '0 0 0 6px rgba(34,197,94,0)',   opacity: 0.82 },
            },
          }),
          ...(isRed && !active && {
            animation: 'audioGuidePulseRed 2.2s ease-in-out infinite',
            '@keyframes audioGuidePulseRed': {
              '0%, 100%': { boxShadow: '0 0 0 0 rgba(220,20,60,0.4)', opacity: 1 },
              '50%':       { boxShadow: '0 0 0 6px rgba(220,20,60,0)',  opacity: 0.82 },
            },
          }),
          ...(isPlaying && {
            animation: 'audioGuidePulse 2s ease-in-out infinite',
            '@keyframes audioGuidePulse': {
              '0%, 100%': { boxShadow: '0 0 0 0 rgba(var(--joy-palette-primary-mainChannel) / 0.18)' },
              '50%':       { boxShadow: '0 0 0 6px rgba(var(--joy-palette-primary-mainChannel) / 0)' },
            },
          }),
        }}
      >
        {isPlaying ? (
          <GraphicEqRoundedIcon sx={{
            fontSize: isProminent || isRed ? '1.2rem' : iconSize,
            color: isRed ? '#fff' : isProminent ? 'success.700' : 'primary.500',
            animation: 'audioEqBounce 0.65s ease-in-out infinite alternate',
            '@keyframes audioEqBounce': {
              from: { transform: 'scaleY(0.5)' },
              to:   { transform: 'scaleY(1.0)' },
            },
          }} />
        ) : isPaused ? (
          <PlayArrowRoundedIcon sx={{ fontSize: isProminent || isRed ? '1.2rem' : iconSize, color: isRed ? '#fff' : isProminent ? 'success.600' : 'primary.500' }} />
        ) : (
          <VolumeUpRoundedIcon sx={{ fontSize: isProminent || isRed ? '1.2rem' : iconSize, color: isRed ? '#fff' : isProminent ? 'success.600' : 'text.secondary' }} />
        )}

        <Typography level="body-xs" sx={{
          fontWeight: 600,
          fontSize: size === 'sm' ? '10px' : '11.5px',
          color: isRed
            ? '#fff'
            : isProminent
            ? (active ? 'success.800' : 'success.700')
            : (active ? 'primary.600' : 'text.secondary'),
          whiteSpace: 'nowrap',
          letterSpacing: '0.02em',
        }}>
          {isPlaying ? 'Playing…' : isPaused ? 'Paused' : label}
        </Typography>

        {/* Pause button — visible while playing */}
        {isPlaying && (
          <Tooltip title="Pause" placement="top" arrow>
            <PauseRoundedIcon sx={{ fontSize: '0.9rem', color: 'primary.400' }} />
          </Tooltip>
        )}

        {/* Stop button — visible while playing or paused */}
        {active && (
          <Tooltip title="Stop &amp; reset" placement="top" arrow>
            <StopRoundedIcon
              onClick={handleStop}
              sx={{
                fontSize: '0.85rem',
                color: 'neutral.400',
                ml: 0.2,
                '&:hover': { color: 'danger.400' },
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Tooltip>
  );
}
